/**
 * ⭐⭐⭐ **Salinan bertahan untuk tier satu-tablet (K13)** — supaya tablet bisa MENYALA luring.
 *
 * ### ⛔ Masalah yang ditutupnya
 * Sejak tanggal usaha & peran perangkat datang dari server, tablet yang **dinyalakan** dalam
 * keadaan luring tak punya apa-apa: nol menu, nol tanggal usaha, nol shell. Padahal menjualnya
 * sendiri sudah luring (hitungan lokal + antrean `outbox`). Jadi yang membatasi bukan lagi
 * kemampuannya, melainkan **saat menyalakannya**.
 *
 * ⚠️ Warung yang listriknya baru menyala pukul 07:00 bersamaan dengan internet yang belum pulih
 * adalah keadaan biasa di Indonesia, bukan kasus ekstrem.
 *
 * ### ⛔⛔ Yang di-cache adalah JAWABAN SERVER, bukan tebakan
 * Ini bukan mengembalikan `localStorage.osbpos.role` atau tanggal dari jam tablet. Yang disimpan
 * jawaban yang PERNAH diberikan server — dan untuk tanggal usaha, yang disimpan bahkan **aturannya**
 * (zona + jam pergantian hari), sehingga tablet menghitung jawaban yang sama persis dengan Postgres.
 *
 * ⚠️ Tiap salinan membawa `savedAt`, dan layar **wajib bisa mengatakan** bahwa yang ditampilkan
 * salinan — pengguna berhak tahu ia sedang melihat menu kemarin.
 *
 * ### Kenapa `localStorage`, bukan IndexedDB
 * Alasan yang sama dengan `outbox.ts`: sinkron, tak ada jendela antara "dibaca layar" dan
 * "tersimpan". Isinya kecil (satu katalog ≈ puluhan KB).
 */

interface Wrapped<T> {
  readonly v: T;
  /** ISO. ⚠️ Dari jam perangkat — untuk DITAMPILKAN, tak pernah untuk memutuskan apa pun. */
  readonly savedAt: string;
}

export interface Cached<T> {
  readonly value: T;
  readonly savedAt: string;
}

/**
 * ⛔ Gagal menulis TIDAK boleh menggagalkan apa pun. Kuota penuh atau mode privat adalah alasan
 * kehilangan cache, bukan alasan kehilangan penjualan.
 */
export function cacheSet<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ v: value, savedAt: new Date().toISOString() }));
  } catch {
    /* diabaikan dengan sengaja — lihat komentar di atas */
  }
}

/** ⚠️ Isi yang tak terbaca diperlakukan sebagai TIDAK ADA, bukan sebagai nilai kosong. */
export function cacheGet<T>(key: string): Cached<T> | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return undefined;
    const w = JSON.parse(raw) as Wrapped<T>;
    if (w.v === undefined || typeof w.savedAt !== 'string') return undefined;
    return { value: w.v, savedAt: w.savedAt };
  } catch {
    return undefined;
  }
}

/** Kunci cache — ⚠️ per OUTLET & per kanal: satu tablet bisa dipindah outlet. */
export const CACHE_KEYS = {
  whoAmI: 'osbpos.cache.whoami',
  businessDateRule: (outlet: string) => `osbpos.cache.bdrule.${outlet}`,
  catalog: (outlet: string, channel: string) => `osbpos.cache.catalog.${outlet}.${channel}`,
  /**
   * Verifier PIN luring, per staf. ⛔ Bukan PIN-nya — lihat `@osbpos/domain/offline-pin`.
   *
   * ⚠️ Kode stafnya memang terbaca dari nama kunci. Itu diterima sadar: yang memegang tablet ini
   * toh melihat nama kasir di layar sepanjang shift, dan menyamarkan nama kunci cuma menambah
   * lapisan yang terasa aman tanpa menahan siapa pun.
   */
  offlinePin: (staffCode: string) => `osbpos.cache.pinv.${staffCode}`,
  /** Sesi kas yang sedang berjalan di perangkat ini. */
  shift: (outlet: string, device: string) => `osbpos.cache.shift.${outlet}.${device}`,
  /**
   * ⭐⭐ Layar dapur: daftar stasiun & tiket terakhir yang PERNAH dijawab server.
   *
   * ⛔ Bukan supaya dapur bisa "jalan luring" — tiket BARU tak mungkin datang tanpa jaringan, dan
   * tak ada cache yang bisa mengarangnya. Yang ditutup lubang lain: tablet dapur yang dimuat ulang
   * (atau layarnya mati lalu menyala) saat jaringan sedang putus jadi **kosong total**, dan dapur
   * yang tiba-tiba bersih dibaca "semua sudah keluar".
   *
   * ⚠️⚠️ Karena itu umurnya WAJIB menyolok di layar. Tiket basi yang tak mengaku basi lebih
   * berbahaya daripada layar kosong: yang satu membuat orang memasak pesanan yang sudah dibatalkan,
   * yang satu membuat orang bertanya.
   */
  kitchenStations: (outlet: string) => `osbpos.cache.kds.stations.${outlet}`,
  kitchenTickets: (outlet: string, station: string) =>
    `osbpos.cache.kds.tickets.${outlet}.${station}`,
} as const;

/** Membuang satu salinan. ⭐ Dipakai saat sesi kas DITUTUP — salinan sesi mati harus ikut mati. */
export function cacheDrop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* diabaikan dengan sengaja — sama alasannya dengan `cacheSet` */
  }
}

/**
 * ⭐⭐⭐ **Minta penyimpanan PERSISTEN** — dan ini bukan kosmetik.
 *
 * ### ⛔ Risiko sebenarnya bukan tulisan robek, melainkan PENGUSIRAN
 * `localStorage` di peramban modern **bukan** SharedPreferences: Chromium menyimpannya di
 * **LevelDB** (write-ahead log), Firefox & Safari di **SQLite**. Tulisan per-kunci atomik — mati
 * listrik di tengah `setItem` menghasilkan nilai LAMA atau nilai BARU, tak pernah separuh.
 * SharedPreferences menulis ulang seluruh berkas tanpa jurnal; itu sebabnya ia gampang rusak, dan
 * itu masalah yang berbeda.
 *
 * ⚠️ Yang benar-benar mengancam kita: penyimpanan bawaan berstatus **best-effort**, jadi peramban
 * boleh **mengusirnya** saat disk menipis — dan antrean `outbox` berisi penjualan yang uangnya
 * SUDAH diterima. `persist()` menaikkannya jadi `persistent`, yang tak diusir tanpa tindakan
 * pengguna.
 *
 * ⛔ Dipanggil sekali saat boot, dan **kegagalannya tak menghalangi apa pun**: peramban lama tak
 * punya API-nya, dan menolak jualan karena itu jauh lebih mahal daripada risiko yang dicegahnya.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persist === undefined) return false;
    /* ⚠️ Sudah persisten ⇒ jangan minta lagi: sebagian peramban memunculkan prompt tiap panggilan. */
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
